import { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, assetUrl, formatApiError } from "@/lib/api";

const empty = {
  name: "", description: "", price: 0, compare_at_price: 0,
  stock: 0, category: "jewelry", image_url: "", is_active: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/products?active_only=false");
      setProducts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p, compare_at_price: p.compare_at_price || 0 });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
        stock: Number(form.stock),
      };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-amber-400">Catalog</div>
          <h1 className="font-serif-display text-3xl mt-2">Products</h1>
        </div>
        <Button onClick={openNew} data-testid="new-product-btn"
          className="bg-amber-500 hover:bg-amber-400 text-slate-900">
          <Plus className="h-4 w-4 mr-2" /> New Product
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p, i) => (
          <div key={p.id} className="bg-slate-800/60 border border-white/5 rounded-lg overflow-hidden"
               data-testid={`admin-product-${i}`}>
            <div className="aspect-[4/3] bg-slate-900">
              {p.image_url ? (
                <img src={assetUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-600">
                  <ImageIcon className="h-8 w-8" />
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="font-serif-display text-lg truncate">{p.name}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="font-mono-accent text-sm">₹{Number(p.price).toLocaleString("en-IN")}</span>
                <span className="text-xs text-slate-400">Stock: {p.stock}</span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-product-${i}`}
                  className="text-slate-300 hover:bg-white/5">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid={`delete-product-${i}`}
                      className="text-red-400 hover:bg-red-500/10">
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{p.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p.id)} data-testid={`confirm-delete-${i}`}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing ? "Edit product" : "New product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input required value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                data-testid="product-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                data-testid="product-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Price (₹)</Label>
                <Input type="number" min="0" step="0.01" required value={form.price}
                  onChange={(e) => setForm({...form, price: e.target.value})}
                  data-testid="product-price" />
              </div>
              <div>
                <Label>Compare at</Label>
                <Input type="number" min="0" step="0.01" value={form.compare_at_price || ""}
                  onChange={(e) => setForm({...form, compare_at_price: e.target.value})}
                  data-testid="product-compare-price" />
              </div>
              <div>
                <Label>Stock</Label>
                <Input type="number" min="0" required value={form.stock}
                  onChange={(e) => setForm({...form, stock: e.target.value})}
                  data-testid="product-stock" />
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Input value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value})}
                data-testid="product-category" />
            </div>
            <div>
              <Label>Image</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="Image URL or upload" value={form.image_url}
                  onChange={(e) => setForm({...form, image_url: e.target.value})}
                  data-testid="product-image-url" />
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="product-image-file" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                        disabled={uploading} data-testid="product-image-upload">
                  <Upload className="h-4 w-4 mr-2" /> {uploading ? "…" : "Upload"}
                </Button>
              </div>
              {form.image_url && (
                <img src={assetUrl(form.image_url)} alt="preview"
                     className="mt-2 h-24 w-24 object-cover rounded border border-white/10" />
              )}
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active}
                onCheckedChange={(v) => setForm({...form, is_active: v})}
                data-testid="product-active" />
              <Label>Active (visible on storefront)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-amber-500 text-slate-900 hover:bg-amber-400"
                      data-testid="product-save">
                {editing ? "Save changes" : "Create product"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
